import { type ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';

import { useAuth } from '@/hooks/useAuth';
import { ROUTES } from '@/lib/constants';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';

interface RequireAuthProps {
  children: ReactNode;
}

/**
 * Route guard. If the user is not authenticated, redirects to the
 * landing page. If we're still resolving `whoami`, shows a centred
 * spinner so the redirect doesn't flicker.
 */
export function RequireAuth({ children }: RequireAuthProps) {
  const { status } = useAuth();
  const location = useLocation();

  if (status === 'loading') {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <LoadingSpinner label="Resolving session…" />
      </div>
    );
  }

  if (status !== 'authenticated') {
    // Bounce home; preserve attempted location so we can return after sign-in.
    return <Navigate to={ROUTES.home} replace state={{ from: location.pathname }} />;
  }

  return <>{children}</>;
}
